/**
 *  @organization: Nanocosmos GmbH
 *
 *  Widget for the three maxFrameDelay alerts (23011 / 23012 / 23013). They share one alert type and one
 *  chart - what differs is the title, the info text and the recommended action, all taken from ALERT_MAPPER.
 *
 *  The chart shows the raw frame delay plus a rolling 5 minute median, because the median is what the
 *  detection actually decides on: single spikes do not open a persistent-delay alert, and a stream can be
 *  alerted without a visible spike. Without the median line the chart would contradict the alert.
 */

// React Imports
import React, { useCallback, useMemo, useState } from 'react';
import { PropTypes } from 'prop-types';
import { Line } from 'react-chartjs-2';
import { Tooltip as ReactTooltip } from 'react-tooltip/dist/react-tooltip.umd';
import { CardTitle, Col, Row } from 'reactstrap';
import ReactDOMServer from 'react-dom/server';

// Redux Imports
import { useSelector, useDispatch } from 'react-redux';
import {
   alertFineStartSelector,
   alertFineEndSelector,
   troubleIntervalSelector,
   hashSelector,
   cancelTokenSelector,
   troubleModeSelector,
} from '../../../../../../redux/selectors/selectors';
import { setTroubleshootingAlertFineTimespan } from '../../../../../../redux/actions/general';
import { URL_PARAMS } from '../../../../../../urlSync/urlParamNames';

// External Libraries
import { useQuery } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import moment from 'moment';
import axios from 'axios';

// Custom Imports
import { ALERT_MAPPER, getPrimaryAlertCode } from './AlertDetailsMapper';
import { AlertChartEventsSwitch, withFilteredEventAnnotations } from './AlertChartEvents';
import { ChartExplanation, getThresholdLegendDatasets, withAxisLabelAndLegendHandling } from './AlertChartLegend';
import {
   DETECTION_WINDOW_IN_MINUTES,
   DETECTION_WINDOW_IN_SECONDS,
   EXPECTED_DATAPOINTS_PER_WINDOW,
   MEDIAN_EVALUATION_INTERVAL_IN_SECONDS,
   MFD_MAX_DISPLAY_RANGE_IN_MS,
   MFD_THRESHOLD,
   STALL_COUNT_PER_WINDOW,
   formatThousands,
   getAlertConnectionId,
   getAlertFrameDelayPoints,
   getFrameDelayStats,
   getIntervalWindows,
   getWindowMedians,
   isAudioStreamAlert,
} from './MaxFrameDelayStats';
import {
   ChartWrapper,
   SetChartHeight,
   AlignedIconContainer,
   HeightSetContainer,
   PaddingContainer,
   SimpleFlexItem,
   SimpleFlexRow,
   TwoSideFlexContainer,
   WidgetBodyContainer,
   WidgetContainer,
   WidgetHeaderContainer,
   CenteredMsgShowing,
   BottomMargin,
   StandardContainer,
   Description,
   ValueText,
   CenterContentContainer,
   FlexRow,
   FontWeightedText,
   HintInfo,
} from '../../../../../../styledComponents/styledComponents';
import {
   aggEventWithSuggestedMinChartOptions,
   getOptionsWithAnnotationsEvents,
} from '../../../../../../constants/chartjs';
import {
   CHRONOLOGICAL_DATE_TIME_FORMAT,
   CHRONOLOGICAL_DATE_TIME_FORMAT_DASHED,
   SEARCH_TYPE,
   MAX_DURATION_IN_MS,
} from '../../../constants';
import { Spinner, CsvExportFromApiButton } from '../../../../../common';
import AlertDetailsPanel from './AlertDetailsPanel';
import { DetailTile, TileContainer } from '../../../Styles';
import TextWithToolTip from '../../../TextWithToolTip';
import { E2E } from '../../../../../../constants/general';
import DateTimeSlider from '../../../general/DateTimeSlider';
import { getIntervalStart, getIntervalEnd } from '../../../general/DataProcessing';
import { ALERT_FINE_START_REDUCTION_IN_MINUTES, ALERT_FINE_END_EXTENSION_IN_MINUTES } from '../constants';
import { getHttpHeader } from '../../../../../../util/FetchTools';

const COLOR = {
   OK: '#34d746',
   ULL_AFFECTED: '#ed7d0e',
   ALL_AFFECTED: '#e92b2b',
   STALL: '#8f1616',
   MEDIAN: '#2f6fd0',
   RAW: 'rgba(120,150,190,0.9)',
   PREFILTER: 'rgba(120,120,120,0.7)',
};

const METRIC_PATH = { video: 'video/maxFrameDelay', audio: 'audio/maxFrameDelay' };

const Y_AXIS_LABEL = 'Frame delay [ms]';

// Both series are marked rather than drawn as a plain line, so the markers have to stay small enough not to
// merge into a band on a dense range - the colour is what has to be readable, not the marker itself.
const POINT_RADIUS = 1;

// The median line carries the band it sits in on its markers and only holds one per minute, so they have to
// be readable on their own rather than merging into a band the way the raw datapoints do.
const MEDIAN_POINT_RADIUS = 2;

// The threshold lines span the whole plot, so a hairline is enough to place them - drawn any heavier they
// compete with the data for attention rather than framing it.
const THRESHOLD_LINE_WIDTH = 1;

// The threshold lines are drawn as annotations but described in the legend: as labels on the plot they sit
// on top of each other whenever two bands are close together, which is most of the time.
//
// The labels name what the reader can see - two of them apply to the blue median line, one to the individual
// datapoints - instead of naming the detection's internals. What each line actually decides needs knowledge
// of the detection, so it is not in the legend but in the info tooltip of the widget header, on demand.
const THRESHOLD_LEGEND = [
   {
      label: `Stall threshold (${MFD_THRESHOLD.STALL_MS} ms)`,
      color: COLOR.STALL,
      borderDash: [6, 4],
      explanation: `A single datapoint above this counts as a stall and is the only band a raw datapoint is marked for. Once ${STALL_COUNT_PER_WINDOW} of the ${EXPECTED_DATAPOINTS_PER_WINDOW} datapoints of a ${DETECTION_WINDOW_IN_MINUTES} minute window exceed it, the stall alert opens.`,
   },
   {
      label: `Critical median (${MFD_THRESHOLD.PERSISTENT_DELAY_MS} ms)`,
      color: COLOR.ALL_AFFECTED,
      explanation:
         'While the median line stays above this, the added latency affects every viewer - its markers are red for as long as it does.',
   },
   {
      label: `Elevated median (${MFD_THRESHOLD.PERSISTENT_DELAY_ULL_MS} ms)`,
      color: COLOR.ULL_AFFECTED,
      explanation:
         'Between this and the critical median, regular playback is unaffected - only applications relying on ultra low latency are. The markers of the median line turn orange.',
   },
   {
      label: `Stall gate (${MFD_THRESHOLD.STALL_PREFILTER_MS} ms)`,
      color: COLOR.PREFILTER,
      borderDash: [3, 3],
      explanation:
         'Stalls are ignored while the median line stays below this, so a stream with an otherwise excellent delay is not alerted over a handful of isolated outliers.',
   },
];

// Named under the alert title rather than in the chart tooltip: it explains the alert, not a line, and it is
// the answer to the first question a normal looking stretch inside an alert raises.
const ALERT_WINDOW_NOTE =
   'An alert is raised on several minutes of data at once and stays on for a while after the stream recovered, so parts of the chart inside an alert can look normal.';

// prettier-ignore
const MEDIAN_LINE_EXPLANATION = `The blue line is the median of the preceding ${DETECTION_WINDOW_IN_MINUTES} minutes, drawn once per full minute and once more where the data ends - the same window and the same cadence the detection decides on, not the raw frame delay. Every one of its points is built from a complete window: the datapoints of the ${DETECTION_WINDOW_IN_MINUTES} minutes before the displayed range are fetched for it as well. Its markers carry the band it currently sits in, while a raw datapoint is only marked once it is a stall: single spikes do not open a delay alert, and a stream can be alerted without a visible spike.`;

// key doubles as the tooltip id and as the lookup into the computed stats, hence no spaces or operators
const MFD_DETAILS = [
   {
      key: 'mfdPeakWindowMedian',
      name: `Peak ${DETECTION_WINDOW_IN_MINUTES} min Median`,
      unit: 'ms',
      description: `The highest median any ${DETECTION_WINDOW_IN_MINUTES} minute window of the displayed range reached, i.e. the peak of the blue line. This is the value the detection decides on: at or above ${MFD_THRESHOLD.PERSISTENT_DELAY_MS} ms the critical alert opens, between ${MFD_THRESHOLD.PERSISTENT_DELAY_ULL_MS} ms and ${MFD_THRESHOLD.PERSISTENT_DELAY_MS} ms the ultra low latency one.`,
   },
   {
      key: 'mfdPeakFrameDelay',
      name: 'Peak Frame Delay',
      unit: 'ms',
      description: `The highest single datapoint of the displayed range, i.e. how far the delay reached at its worst. Read against the peak ${DETECTION_WINDOW_IN_MINUTES} minute median: close to it the delay was raised for minutes on end, far above it the problem was a spike.`,
   },
   {
      key: 'mfdPeakWindowStalls',
      name: `Peak ${DETECTION_WINDOW_IN_MINUTES} min Stalls`,
      unit: '',
      description: `The most datapoints at or above ${MFD_THRESHOLD.STALL_MS} ms that any ${DETECTION_WINDOW_IN_MINUTES} minute window of the displayed range held. The stall alert opens once at least ${STALL_COUNT_PER_WINDOW} of the ${EXPECTED_DATAPOINTS_PER_WINDOW} datapoints of a window exceed that, and only while the window median is above ${MFD_THRESHOLD.STALL_PREFILTER_MS} ms.`,
   },
   {
      key: 'mfdUllShare',
      name: `ULL Datapoints (>= ${MFD_THRESHOLD.PERSISTENT_DELAY_ULL_MS} ms)`,
      unit: '%',
      description:
         'Share of datapoints at or above the ultra low latency threshold within the displayed range - how often ultra low latency playback was affected at all.',
   },
   {
      key: 'mfdTrack',
      name: 'Evaluated Track',
      unit: '',
      description:
         'The track the alerting service evaluated. Audio is used for audio only streams, video for every stream that carries a video track.',
   },
];

// The stall threshold is the only rule the detection applies to a single datapoint, so it is the only band a
// raw datapoint is marked for. Painting a spike in the critical colour would read as critical where the
// detection reads fine: a stream with a healthy median is not alerted over a handful of outliers.
function getDatapointColor(frameDelay) {
   return frameDelay >= MFD_THRESHOLD.STALL_MS ? COLOR.STALL : COLOR.RAW;
}

// The two delay alerts open on the window median, so the bands belong to the markers of the median line. The
// line itself stays blue, which keeps it recognisable while its markers change colour.
function getMedianColor(medianFrameDelay) {
   if (medianFrameDelay >= MFD_THRESHOLD.PERSISTENT_DELAY_MS) return COLOR.ALL_AFFECTED;
   if (medianFrameDelay >= MFD_THRESHOLD.PERSISTENT_DELAY_ULL_MS) return COLOR.ULL_AFFECTED;
   return COLOR.OK;
}

// no label on purpose - the legend names the thresholds, see THRESHOLD_LEGEND
function getThresholdAnnotation(value, color, borderDash) {
   return {
      type: 'line',
      mode: 'horizontal',
      scaleID: 'y-axis-0',
      value,
      borderColor: color,
      borderWidth: THRESHOLD_LINE_WIDTH,
      borderDash: borderDash || undefined,
   };
}

function getEventAnnotation(value, content, yAdjust) {
   return {
      type: 'line',
      mode: 'vertical',
      scaleID: 'x-axis-0',
      value,
      borderColor: 'black',
      borderWidth: 1.5,
      label: {
         backgroundColor: 'rgba(0,0,0, 0.5)',
         content,
         enabled: true,
         position: 'bottom',
         yAdjust,
      },
   };
}

function getAnnotations(selectedAlert, isStreamFinishedAfterAlert) {
   const startAndEndIsSame = selectedAlert.first === selectedAlert.last;
   const alertEnd =
      selectedAlert.status === 'Terminated'
         ? moment.utc(selectedAlert.last, CHRONOLOGICAL_DATE_TIME_FORMAT).format()
         : undefined;

   const annotations = [
      getThresholdAnnotation(MFD_THRESHOLD.STALL_MS, COLOR.STALL, [6, 4]),
      getThresholdAnnotation(MFD_THRESHOLD.PERSISTENT_DELAY_MS, COLOR.ALL_AFFECTED),
      getThresholdAnnotation(MFD_THRESHOLD.PERSISTENT_DELAY_ULL_MS, COLOR.ULL_AFFECTED),
      getThresholdAnnotation(MFD_THRESHOLD.STALL_PREFILTER_MS, COLOR.PREFILTER, [3, 3]),
      getEventAnnotation(
         moment
            .utc(selectedAlert.first, CHRONOLOGICAL_DATE_TIME_FORMAT)
            .subtract(DETECTION_WINDOW_IN_SECONDS, 'seconds')
            .format(),
         'Detection Start',
         75,
      ),
      getEventAnnotation(
         moment.utc(selectedAlert.first, CHRONOLOGICAL_DATE_TIME_FORMAT).format(),
         startAndEndIsSame ? 'Emitted and stopped Alert' : 'Emitted Alert',
         51,
      ),
      getEventAnnotation(alertEnd, isStreamFinishedAfterAlert ? 'Stream stopped' : 'Stream recovered', 3),
   ];

   if (!startAndEndIsSame) {
      annotations.push(getEventAnnotation(alertEnd, 'Alert stopped', 27));
   }

   return annotations;
}

// the raw timeseries route takes its bounds as plain UTC datetimes without a zone suffix
function toQueryTime(dateTimeString, minutesBefore = 0) {
   return moment
      .utc(dateTimeString, CHRONOLOGICAL_DATE_TIME_FORMAT)
      .subtract(minutesBefore, 'minutes')
      .format('YYYY-MM-DDTHH:mm:ss');
}

function MaxFrameDelayWidget({ streamName, selectedAlert, height }) {
   const ALERT_CODE = getPrimaryAlertCode(selectedAlert.codes);
   // a maxFrameDelay code the frontend does not know yet must not blank the page, so the widget degrades to
   // the chart without the code specific texts
   const { title, alertInfo, maxRtmpStatDisplayRangeInMS = MFD_MAX_DISPLAY_RANGE_IN_MS } =
      ALERT_MAPPER[ALERT_CODE] || {};

   // the alerting service evaluates the audio track for audio only streams, so charting the video metric
   // would show an empty series underneath an alert that clearly fired
   const isAudioStream = isAudioStreamAlert(selectedAlert.isAudioStream);
   const metricPath = isAudioStream ? METRIC_PATH.audio : METRIC_PATH.video;

   // --------------------------
   // ## React useDispatch hook (REDUX)
   // --------------------------

   const dispatch = useDispatch();

   // --------------------------
   // ## React useSelector hooks
   // --------------------------

   const hash = useSelector(hashSelector);
   const cancelToken = useSelector(cancelTokenSelector);

   const alertFineStart = useSelector(alertFineStartSelector);
   const alertFineStartMoment = moment.utc(alertFineStart, CHRONOLOGICAL_DATE_TIME_FORMAT);

   const alertFineEnd = useSelector(alertFineEndSelector);
   const alertFineEndMoment = moment.utc(alertFineEnd, CHRONOLOGICAL_DATE_TIME_FORMAT);

   const interval = useSelector(troubleIntervalSelector);
   const mode = useSelector(troubleModeSelector);

   // --------------------------
   // ## React useState hooks
   // --------------------------

   const [areEventsShown, setAreEventsShown] = useState(true);

   // --------------------------
   // ## React useMemo hooks
   // --------------------------

   const header = useMemo(() => getHttpHeader(hash), [hash]);

   // --------------------------
   // ## React useCallback hooks
   // --------------------------

   const setAlertFineInterval = useCallback(
      (fineInterval) => {
         dispatch(
            setTroubleshootingAlertFineTimespan(
               { alertFineStart: fineInterval.fineStart, alertFineEnd: fineInterval.fineEnd },
               {
                  [URL_PARAMS.ALERT_FINE_START]: fineInterval.fineStart,
                  [URL_PARAMS.ALERT_FINE_END]: fineInterval.fineEnd,
               },
            ),
         );
      },
      [dispatch],
   );

   // --------------------------
   // ## React useQuery hooks
   // --------------------------

   const diff = alertFineEndMoment.diff(alertFineStartMoment, 'milliseconds');
   const alertDurationCanBeDisplayed = diff < MAX_DURATION_IN_MS;
   const isQueryEnabled = alertFineStart !== '' && alertFineEnd !== '' && alertDurationCanBeDisplayed;

   const RAW_TIMESERIES_URL = `/v2/troubleshooting/rtmp/${metricPath}/raw/timeseries`;
   // the displayed range, which is what the axis, the tiles and the CSV export are built from
   // prettier-ignore
   const RAW_TIMESERIES_PARAMS = `streams=${streamName}&from=${toQueryTime(alertFineStart)}&to=${toQueryTime(alertFineEnd)}`;
   // One window more than that is requested: a median only says what the detection decided on once its whole
   // window is covered by data, so the datapoints of the window before the displayed range are needed too.
   // They feed the median line alone - they are neither drawn nor counted in the tiles.
   // prettier-ignore
   const MEDIAN_LOOKBACK_PARAMS = `streams=${streamName}&from=${toQueryTime(alertFineStart, DETECTION_WINDOW_IN_MINUTES)}&to=${toQueryTime(alertFineEnd)}`;

   const { isLoading, isFetching, data: rtmpStats } = useQuery({
      enabled: isQueryEnabled,
      // every value the request is built from belongs into the key, or a cached response outlives the range,
      // the track or the stream it was fetched for
      queryKey: ['alertMaxFrameDelay', streamName, metricPath, alertFineStart, alertFineEnd],
      queryFn: async () => {
         // no interval parameter: the raw route answers with the unaggregated datapoints the detection
         // evaluates, which is what the median line and the tiles below have to be computed from
         const url = `${RAW_TIMESERIES_URL}?${MEDIAN_LOOKBACK_PARAMS}&extendedDatapoint=true`;
         return axios.get(url, { headers: header }).then((res) => res.data);
      },
      onError: (err) => toast.error(err),
      retry: false,
      refetchOnWindowFocus: false,
   });

   // --------------------------
   // ## React useMemo hooks (chart)
   // --------------------------

   const { chartData, baseChartOptions, stats } = useMemo(() => {
      const emptyResult = { chartData: { labels: [], datasets: [] }, baseChartOptions: {}, stats: null };

      if (!(rtmpStats?.data?.length > 0)) return emptyResult;

      const alertConnectionId = getAlertConnectionId(selectedAlert.connectionsDetails, selectedAlert.last);
      const alertEndUnixSec = moment.utc(selectedAlert.last).unix();

      const xStart = moment.utc(alertFineStart, CHRONOLOGICAL_DATE_TIME_FORMAT).valueOf();
      const xEnd = moment.utc(alertFineEnd, CHRONOLOGICAL_DATE_TIME_FORMAT).valueOf();

      // Not every datapoint of the response belongs to the alert: the range deliberately reaches beyond it,
      // and a stream that was restarted inside the range answers with the datapoints of every connection it
      // ran on - only the one the alert was raised on is what the detection decided on.
      const pointsWithLookback = getAlertFrameDelayPoints(rtmpStats.data, alertConnectionId);
      // the window fetched ahead of the range feeds the median alone, everything else is drawn and counted
      const points = pointsWithLookback.filter((point) => point.x >= xStart);

      if (points.length === 0) return emptyResult;

      // no datapoint after the alert closed means the stream ended rather than recovered
      const isStreamFinishedAfterAlert = !rtmpStats.data.some((datapoint) => datapoint.timestamp > alertEndUnixSec);

      const options = withAxisLabelAndLegendHandling(
         getOptionsWithAnnotationsEvents(
            aggEventWithSuggestedMinChartOptions(xStart, xEnd, Y_AXIS_LABEL, interval.stepsInMilliSec / 1000, 0),
            getAnnotations(selectedAlert, isStreamFinishedAfterAlert),
         ),
         { axisLabel: Y_AXIS_LABEL, formatValue: (value) => `${formatThousands(value)} ms`, realDatasetCount: 2 },
      );

      const datapointColors = points.map((point) => getDatapointColor(point.y));
      const windows = getIntervalWindows(
         pointsWithLookback,
         DETECTION_WINDOW_IN_SECONDS,
         MEDIAN_EVALUATION_INTERVAL_IN_SECONDS,
      );
      const medians = getWindowMedians(windows);
      const medianColors = medians.map((point) => getMedianColor(point.y));

      // the unit lives on the axis and in the tooltip, so the dataset labels stay free of it
      const rawDataset = {
         label: 'Frame delay',
         borderColor: 'rgba(120,150,190,0.65)',
         borderWidth: 1,
         // uniform size - whether a datapoint is a stall is carried by its colour alone
         pointRadius: POINT_RADIUS,
         pointBackgroundColor: datapointColors,
         pointBorderColor: datapointColors,
         data: points,
         fill: false,
         showLine: true,
         cubicInterpolationMode: 'monotone',
      };

      const medianDataset = {
         label: `Median per minute (${DETECTION_WINDOW_IN_MINUTES} min window)`,
         borderColor: COLOR.MEDIAN,
         borderWidth: 2,
         pointRadius: MEDIAN_POINT_RADIUS,
         pointBackgroundColor: medianColors,
         pointBorderColor: medianColors,
         data: medians,
         fill: false,
         showLine: true,
      };

      return {
         chartData: {
            labels: [],
            datasets: [rawDataset, medianDataset, ...getThresholdLegendDatasets(THRESHOLD_LEGEND)],
         },
         baseChartOptions: options,
         stats: getFrameDelayStats(points, windows, isAudioStream),
      };
   }, [rtmpStats, selectedAlert, interval, alertFineStart, alertFineEnd, isAudioStream]);

   // Kept apart from the data: the switch hides four markers, so folding it into the memo above would
   // recompute every dataset and the rolling median of the whole series on each click.
   const chartOptions = useMemo(() => withFilteredEventAnnotations(baseChartOptions, areEventsShown), [
      baseChartOptions,
      areEventsShown,
   ]);

   // --------------------------
   // ## Constants / Variables / Functions
   // --------------------------

   const CSV_EXPORT_URL = `${RAW_TIMESERIES_URL}?${RAW_TIMESERIES_PARAMS}`;
   // prettier-ignore
   const FILE_NAME = `${moment.utc().format(CHRONOLOGICAL_DATE_TIME_FORMAT_DASHED)}_alert_mfd_id-${selectedAlert.id}_from-${moment.utc(alertFineStart, CHRONOLOGICAL_DATE_TIME_FORMAT).format(CHRONOLOGICAL_DATE_TIME_FORMAT_DASHED)}_to-${moment.utc(alertFineEnd, CHRONOLOGICAL_DATE_TIME_FORMAT).format(CHRONOLOGICAL_DATE_TIME_FORMAT_DASHED)}`;

   const dataStillInTimeRange = moment
      .utc(selectedAlert.first, CHRONOLOGICAL_DATE_TIME_FORMAT)
      .isSameOrAfter(moment.utc().subtract(2, 'weeks'));

   const sliderRangeInMS = alertFineEndMoment.diff(alertFineStartMoment, 'milliseconds');
   const sliderRangeIsValid = sliderRangeInMS <= maxRtmpStatDisplayRangeInMS;

   const adjustedFineStart = moment
      .utc(selectedAlert.first, CHRONOLOGICAL_DATE_TIME_FORMAT)
      .subtract(ALERT_FINE_START_REDUCTION_IN_MINUTES, 'minutes')
      .format(CHRONOLOGICAL_DATE_TIME_FORMAT);

   const adjustedFineEnd = moment
      .utc(selectedAlert.last, CHRONOLOGICAL_DATE_TIME_FORMAT)
      .add(ALERT_FINE_END_EXTENSION_IN_MINUTES, 'minutes')
      .format(CHRONOLOGICAL_DATE_TIME_FORMAT);

   return (
      <>
         {alertFineStart !== '' && alertFineEnd !== '' && (
            <BottomMargin pxSize={15}>
               <DateTimeSlider
                  startDateString={getIntervalStart(adjustedFineStart, interval.value)}
                  endDateString={getIntervalEnd(adjustedFineEnd, interval.value)}
                  fineStartString={alertFineStart}
                  fineEndString={alertFineEnd}
                  setFineInterval={setAlertFineInterval}
                  interval={interval}
                  searchType={SEARCH_TYPE.ALERT}
                  maxTimespanInMS={maxRtmpStatDisplayRangeInMS}
               />
               <BottomMargin pxSize={15} />
               <FlexRow>
                  <CenterContentContainer>
                     <HintInfo>
                        <FontWeightedText fontWeight={600}>
                           Note, that the displayed time range additionally includes:
                        </FontWeightedText>
                        <ul>
                           <li>
                              <span
                                 style={{ fontWeight: 600 }}
                              >{`${ALERT_FINE_START_REDUCTION_IN_MINUTES} minutes before`}</span>{' '}
                              the initial alert detection.
                           </li>
                           <li>
                              up to{' '}
                              <span
                                 style={{ fontWeight: 600 }}
                              >{`${ALERT_FINE_END_EXTENSION_IN_MINUTES} minutes after`}</span>{' '}
                              the alert was resolved.
                           </li>
                        </ul>
                     </HintInfo>
                  </CenterContentContainer>
               </FlexRow>
            </BottomMargin>
         )}
         <PaddingContainer>
            <WidgetContainer $margin={'0px 0px 0px 0px'}>
               <WidgetHeaderContainer>
                  <HeightSetContainer $heightInPx={23}>
                     <TwoSideFlexContainer>
                        <SimpleFlexRow>
                           <SimpleFlexItem>
                              <CsvExportFromApiButton
                                 url={CSV_EXPORT_URL}
                                 header={header}
                                 cancelToken={cancelToken}
                                 filename={FILE_NAME}
                              />
                           </SimpleFlexItem>
                           <SimpleFlexItem>
                              <AlertChartEventsSwitch
                                 areEventsShown={areEventsShown}
                                 setAreEventsShown={setAreEventsShown}
                              />
                           </SimpleFlexItem>
                        </SimpleFlexRow>
                        <SimpleFlexRow>
                           <SimpleFlexItem>
                              <AlignedIconContainer
                                 data-tooltip-id='MFD'
                                 data-tooltip-html={ReactDOMServer.renderToStaticMarkup(
                                    <ChartExplanation
                                       alertInfo={alertInfo}
                                       chartNote={MEDIAN_LINE_EXPLANATION}
                                       thresholds={THRESHOLD_LEGEND}
                                    />,
                                 )}
                                 className='bi bi-info-circle ml-1'
                              ></AlignedIconContainer>
                              <ReactTooltip
                                 id='MFD'
                                 place={'top'}
                                 className={'reduced-line-height bring-on-top'}
                                 backgroundColor={'rgba(0,0,0,0.9)'}
                                 textColor={'rgb(240,240,240)'}
                                 style={{ textAlign: 'center', maxWidth: '400px' }}
                                 clickable
                              />
                           </SimpleFlexItem>
                        </SimpleFlexRow>
                     </TwoSideFlexContainer>
                  </HeightSetContainer>
               </WidgetHeaderContainer>
               <WidgetBodyContainer>
                  <Row>
                     <Col>
                        <CardTitle className='mb-0'>{title}</CardTitle>
                        <div className='small text-muted'>{`From ${alertFineStart} to ${alertFineEnd} \u00A0\u00A0( UTC time )`}</div>
                        {/* Two mechanisms put healthy looking data inside an alert and neither is visible in
                            the chart: the detection decides on several minutes at once, so a stall minutes ago
                            still counts, and the alerting holds an alert open for a while after the condition
                            cleared - the end of the alert is the end of that grace, not of the problem. Both
                            are configured server side, hence a sentence rather than numbers. */}
                        <div className='small text-muted'>{ALERT_WINDOW_NOTE}</div>
                     </Col>
                  </Row>
                  <BottomMargin pxSize={20}>
                     {!isFetching && stats !== null && sliderRangeIsValid && (
                        <StandardContainer $paddingPx={15}>
                           <TileContainer gapPx={20} isFullScreen={mode !== E2E}>
                              {MFD_DETAILS.map(({ key, name, unit, description }) => (
                                 <div key={key}>
                                    <DetailTile
                                       heightPx={60}
                                       gapPx={10}
                                       style={{ cursor: 'pointer' }}
                                       data-tooltip-id={key}
                                       data-tooltip-html={ReactDOMServer.renderToStaticMarkup(<div>{description}</div>)}
                                    >
                                       <Description marginLeftPx={0} style={{ cursor: 'pointer' }}>
                                          {name}
                                       </Description>
                                       <ValueText>
                                          <TextWithToolTip
                                             text={
                                                stats[key] === null
                                                   ? '-'
                                                   : `${formatThousands(stats[key])}${unit ? ` ${unit}` : ''}`
                                             }
                                             idName={key}
                                          />
                                       </ValueText>
                                    </DetailTile>
                                    <ReactTooltip
                                       id={key}
                                       place={'top'}
                                       className={'reduced-line-height bring-on-top'}
                                       backgroundColor={'rgba(0,0,0,0.9)'}
                                       textColor={'rgb(240,240,240)'}
                                       style={{ textAlign: 'center', maxWidth: '400px' }}
                                       clickable
                                    />
                                 </div>
                              ))}
                           </TileContainer>
                        </StandardContainer>
                     )}
                     <ChartWrapper className='chart-wrapper' height={height} marginTopPx={10}>
                        <Spinner loading={(isLoading || isFetching) && isQueryEnabled} parentTopMarginPx={60}>
                           {!sliderRangeIsValid ? (
                              <CenteredMsgShowing height={height} marginTopPx={20}>
                                 {'Selected time range is too big!'}
                                 <>
                                    <br />
                                    {`Please reduce the selected range to ${
                                       maxRtmpStatDisplayRangeInMS / 1000 / 60 / 60
                                    } hours!`}
                                 </>
                              </CenteredMsgShowing>
                           ) : (
                              <>
                                 {chartData.datasets.length === 0 ? (
                                    <CenteredMsgShowing height={height} marginTopPx={20}>
                                       <>
                                          {'No data available'}
                                          {!dataStillInTimeRange && (
                                             <>
                                                <br />
                                                {'Details are only available for 14 days!'}
                                             </>
                                          )}
                                       </>
                                    </CenteredMsgShowing>
                                 ) : (
                                    <SetChartHeight height={height}>
                                       <Line data={chartData} options={chartOptions}></Line>
                                    </SetChartHeight>
                                 )}
                              </>
                           )}
                        </Spinner>
                     </ChartWrapper>
                  </BottomMargin>
                  <AlertDetailsPanel selectedAlert={selectedAlert} />
               </WidgetBodyContainer>
            </WidgetContainer>
         </PaddingContainer>
      </>
   );
}

MaxFrameDelayWidget.propTypes = {
   selectedAlert: PropTypes.object,
   streamName: PropTypes.string,
   height: PropTypes.number,
};

MaxFrameDelayWidget.defaultProps = {
   height: 300,
};

export default MaxFrameDelayWidget;
